import { useToast } from "@/components/ui/use-toast";
import {
  ExpenseReportByView,
  ExpenseReportStatus,
  ExpenseReportsByViewDocument,
  useApproveOrRejectExpenseReportMutation,
} from "@common/utils/graphql";
import { useSubmitDraftReport } from "./useSubmitDraftReport";

const getReviewedErrorToastMessage = (action: ExpenseReportStatus) => {
  switch (action) {
    case ExpenseReportStatus.Approved:
      return "Failed to approve report, please try again";
    case ExpenseReportStatus.Rejected:
      return "Failed to reject report, please try again";
    case ExpenseReportStatus.PartiallyApproved:
      return "Failed to partially approve report, please try again";
    default:
      return "Failed to save report review, please try again";
  }
};

const getReviewedSuccessToastMessage = (action: ExpenseReportStatus) => {
  switch (action) {
    case ExpenseReportStatus.Approved:
      return "Report approved";
    case ExpenseReportStatus.Rejected:
      return "Report rejected";
    case ExpenseReportStatus.PartiallyApproved:
      return "Report partially approved";
    default:
      return "Report reviewed successfully";
  }
};

export const useReportActions = (
  report: ExpenseReportByView,
  approvedIds: number[],
  onSubmitted?: () => void,
) => {
  const { toast } = useToast();

  const [submitApproveRejectExpenseReport, { loading: isSubmittingApproval }] =
    useApproveOrRejectExpenseReportMutation({
      refetchQueries: [ExpenseReportsByViewDocument],
    });

  const {
    onSubmitDraftReport,
    isHidden,
    loading: isSubmittingDraft,
    isReportOutOfPolicy,
  } = useSubmitDraftReport(report.id);

  const onSubmitReviewedReport = async (
    action: ExpenseReportStatus,
    notes?: string,
  ) => {
    try {
      const { data } = await submitApproveRejectExpenseReport({
        variables: {
          approvedExpenseIds: approvedIds,
          employeeId: report.employeeId,
          reportId: report.id,
          employerNotes: notes,
        },
      });
      if (
        data?.approveOrRejectExpenseReport.__typename !==
        "MutationApproveOrRejectExpenseReportSuccess"
      ) {
        throw new Error();
      }
      toast({
        title: getReviewedSuccessToastMessage(action),
      });
      onSubmitted?.();
    } catch (e) {
      toast({
        variant: "destructive",
        title: getReviewedErrorToastMessage(action),
      });
    }
  };

  return {
    onSubmitReport: onSubmitDraftReport,
    isHidden,
    onSubmitReviewedReport,
    loading: isSubmittingDraft || isSubmittingApproval,
    isReportOutOfPolicy,
  };
};
